import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pill, Activity, ShieldCheck, Stethoscope, Calculator, ChevronRight, Mail, Phone, MapPin } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div style={{ fontFamily: 'var(--font-sans, Inter, system-ui)', color: '#1e293b', background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* ── NAVBAR ────────────────────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, width: '100%',
        background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(226,232,240,0.8)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 5%',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <div style={{ width: 36, height: 36, background: '#1a6ef5', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Pill size={20} color="white" />
          </div>
          <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em' }}>
            MediDose
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ display: 'flex', gap: 24 }} className="hide-on-mobile">
            <a href="#about" style={{ textDecoration: 'none', color: '#64748b', fontWeight: 600, fontSize: '0.9375rem', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color = '#0f172a'} onMouseLeave={e => e.target.style.color = '#64748b'}>About</a>
            <a href="#features" style={{ textDecoration: 'none', color: '#64748b', fontWeight: 600, fontSize: '0.9375rem', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color = '#0f172a'} onMouseLeave={e => e.target.style.color = '#64748b'}>Features</a>
            <a href="#contact" style={{ textDecoration: 'none', color: '#64748b', fontWeight: 600, fontSize: '0.9375rem', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color = '#0f172a'} onMouseLeave={e => e.target.style.color = '#64748b'}>Contact</a>
          </div>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: '#1a6ef5', color: 'white', border: 'none', borderRadius: 8,
              padding: '10px 20px', fontSize: '0.9375rem', fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(26,110,245,0.2)'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(26,110,245,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(26,110,245,0.2)'; }}
          >
            Login <ChevronRight size={16} />
          </button>
        </div>
      </nav>

      {/* ── HERO SECTION ──────────────────────────────────────────────────── */}
      <section style={{
        padding: '160px 5% 100px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
        background: 'linear-gradient(140deg, #f0f5ff 0%, #ffffff 100%)', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', top: -100, left: -100, width: 400, height: 400, background: '#1a6ef5', filter: 'blur(100px)', opacity: 0.1, borderRadius: '50%' }} />
        <div style={{ position: 'absolute', bottom: 100, right: -100, width: 300, height: 300, background: '#7c3aed', filter: 'blur(100px)', opacity: 0.08, borderRadius: '50%' }} />

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#e0e7ff', color: '#4338ca', padding: '6px 16px', borderRadius: 100, fontSize: '0.8125rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 24 }}>
          <Activity size={14} /> Clinical Decision Support System
        </div>

        <h1 style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.1, letterSpacing: '-0.03em', maxWidth: 900, margin: '0 0 24px' }}>
          Precision Dosing for <span style={{ color: '#1a6ef5' }}>Safer Healthcare</span>
        </h1>

        <p style={{ fontSize: 'clamp(1.1rem, 2vw, 1.25rem)', color: '#475569', lineHeight: 1.6, maxWidth: 700, margin: '0 0 40px' }}>
          Empowering doctors, pharmacists, and nursing staff with reliable, medical-proven formulas to compute accurate medication dosages and ensure patient safety.
        </p>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: '#1a6ef5', color: 'white', border: 'none', borderRadius: 12,
              padding: '16px 32px', fontSize: '1.0625rem', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s', boxShadow: '0 8px 20px rgba(26,110,245,0.25)',
              display: 'flex', alignItems: 'center', gap: 8
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 24px rgba(26,110,245,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(26,110,245,0.25)'; }}
          >
            Go to Calculators
          </button>
          <button
            onClick={() => { document.getElementById('about').scrollIntoView({ behavior: 'smooth' }); }}
            style={{
              background: 'white', color: '#1a6ef5', border: '2px solid #e2e8f0', borderRadius: 12,
              padding: '14px 32px', fontSize: '1.0625rem', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 8
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#f8fafc'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
          >
            Learn More
          </button>
        </div>
      </section>

      {/* ── ABOUT / FEATURES ──────────────────────────────────────────────── */}
      <section id="features" style={{ padding: '80px 5%', background: '#fff' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <h2 style={{ fontSize: '2.25rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', margin: '0 0 16px' }}>
              Why Choose MediDose?
            </h2>
            <p style={{ fontSize: '1.125rem', color: '#64748b', maxWidth: 600, margin: '0 auto' }}>
              Designed by clinicians for clinicians, built into an easy-to-use platform that minimizes human error.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 32 }}>
            {[
              {
                icon: Calculator, color: '#1a6ef5', bg: '#eff6ff', title: 'Proven Formulas',
                desc: 'Access a wide array of standardized calculations for adult & pediatric populations, tailored to BMI, BSA, and renal clearance.'
              },
              {
                icon: ShieldCheck, color: '#10b981', bg: '#ecfdf5', title: 'Clinical Safety Checks',
                desc: 'Built-in dose boundaries and warning systems instantly alert you if a calculated dose exceeds accepted safety maximums.'
              },
              {
                icon: Stethoscope, color: '#8b5cf6', bg: '#f5f3ff', title: 'Detailed Medication Rules',
                desc: 'Search our integrated database of medicines and auto-apply complex, weight and age-based dosage rules instantly.'
              }
            ].map((feat, i) => (
              <div key={i} style={{
                padding: 40, borderRadius: 24, border: '1px solid #e2e8f0', background: '#fff',
                transition: 'transform 0.3s, box-shadow 0.3s', cursor: 'default'
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.05)'; e.currentTarget.style.borderColor = feat.color + '40'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
              >
                <div style={{ width: 56, height: 56, borderRadius: 16, background: feat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
                  <feat.icon size={28} color={feat.color} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 12px', color: '#0f172a' }}>{feat.title}</h3>
                <p style={{ color: '#64748b', lineHeight: 1.6, margin: 0 }}>{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT ─────────────────────────────────────────────────────────── */}
      <section id="about" style={{ padding: '80px 5%', background: '#0f172a', color: 'white' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.25rem', fontWeight: 800, margin: '0 0 24px', letterSpacing: '-0.02em' }}>
            About MediDose
          </h2>
          <p style={{ fontSize: '1.125rem', color: '#cbd5e1', lineHeight: 1.8, marginBottom: 24 }}>
            MediDose was conceived to mitigate the risks associated with manual medication calculations in busy
            hospital environments. By digitizing complex clinical guidelines and pharmacokinetic protocols, we simplify
            the workflow for healthcare staff and significantly reduce cognitive load.
          </p>
          <p style={{ fontSize: '1.125rem', color: '#cbd5e1', lineHeight: 1.8 }}>
            Whether you are calculating a pediatric suspension dose, managing a critical continuous infusion, or scaling
            based on compromised renal function, MediDose acts as your reliable mathematical assistant.
          </p>
        </div>
      </section>

      {/* ── CONTACT ───────────────────────────────────────────────────────── */}
      <section id="contact" style={{ padding: '80px 5%', background: '#fff' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.25rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', margin: '0 0 16px' }}>
            Get In Touch
          </h2>
          <p style={{ fontSize: '1.125rem', color: '#64748b', marginBottom: 40 }}>
            Interested in deploying MediDose at your institution or need technical support?
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 40px', background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0', width: '100%', maxWidth: 500, justifyContent: 'center' }}>
              <Mail size={24} color="#1a6ef5" />
              <span style={{ fontSize: '1.125rem', fontWeight: 600, color: '#334155' }}>raviswami899@gmail.com</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 40px', background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0', width: '100%', maxWidth: 500, justifyContent: 'center' }}>
              <Phone size={24} color="#10b981" />
              <span style={{ fontSize: '1.125rem', fontWeight: 600, color: '#334155' }}>+91 9649729899</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 40px', background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0', width: '100%', maxWidth: 500, justifyContent: 'center' }}>
              <MapPin size={24} color="#8b5cf6" />
              <span style={{ fontSize: '1.125rem', fontWeight: 600, color: '#334155' }}>Motilal Nehru National Institute of Technology, Allahabad, Uttar Pradesh, India - 211004</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <footer style={{ padding: '40px 5%', background: '#f1f5f9', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Pill size={18} color="#94a3b8" />
          <span style={{ fontWeight: 700, color: '#64748b', fontSize: '1.125rem' }}>MediDose</span>
        </div>
        <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
          &copy; {new Date().getFullYear()} MediDose Inc. All rights reserved. <br />
          <em>A Clinical Decision Support System. Not a substitute for professional medical judgment.</em>
        </p>
      </footer>

      {/* Custom styles to handle mobile hiding */}
      <style>{`
        @media (max-width: 640px) {
          .hide-on-mobile { display: none !important; }
        }
      `}</style>
    </div>
  );
}
